import '~/modules/label-perf/styles/label-perf-override.css';

import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import type { ClassNamesConfig, GroupBase, SelectInstance } from 'react-select';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';

import { URL_PREFIX } from '../../constants/api';
import type { DataResponse } from '../DataGrid/types';

export type DropdownSelectRef = React.Ref<
  SelectInstance<DropdownOption, boolean, GroupBase<DropdownOption>>
>;

export interface DropdownOption {
  value: string;
  label: string;
}

export interface DropdownProps<T, Q = unknown> {
  options?: T[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  components?: any;
  value?:
    | string
    | string[]
    | number
    | number[]
    | DropdownOption
    | DropdownOption[];
  placeholder?: string;
  className?: string;
  isValid?: boolean;
  isAsync?: boolean;
  isCreatable?: boolean;
  isReadonly?: boolean;
  isSearchable?: boolean;
  isMulti?: boolean;
  asyncEndpoint?: string;
  asyncBody?: Q;
  asyncSearchField?: string;
  creatableRegExpValidator?: RegExp;
  asyncSearchSerializeFn?: (searchValue: string) => string | undefined;
  onChange: (value: T | T[]) => void;
  onLoadOptions?: (options: T[]) => void;
  noOptionsMessage?: string;
}

const createOption = (label: string): DropdownOption => ({
  label,
  value: label,
});

function DropdownInner<T extends DropdownOption, Q = unknown>(
  {
    options,
    value,
    placeholder = 'Select...',
    className = '',
    isValid = true,
    components,
    isAsync,
    isSearchable = true,
    isCreatable = false,
    isMulti = false,
    isReadonly = false,
    asyncEndpoint,
    asyncBody,
    asyncSearchField,
    creatableRegExpValidator,
    asyncSearchSerializeFn,
    onChange,
    onLoadOptions,
    noOptionsMessage,
  }: DropdownProps<T, Q>,
  ref: React.ForwardedRef<SelectInstance<T, boolean, GroupBase<T>>>,
) {
  const [creatableOptions, setCreatableOptions] = useState<DropdownOption[]>(
    [],
  );
  const [error, setError] = useState<string>('');
  const initLoadRef = useRef(true);

  useEffect(() => {
    if (isCreatable) {
      setCreatableOptions(options as DropdownOption[]);
    }
  }, [options, isCreatable]);

  const handleChange = (_value: DropdownOption | DropdownOption[]): void => {
    const selectedValue = _value as T;

    onChange(selectedValue);
  };

  const getDefaultValue = (
    _isMulti: boolean,
    _value: string | string[] | number,
    _options?: DropdownOption[],
  ): DropdownOption | DropdownOption[] => {
    const hasOptions = !!_options?.length;
    let customValue;

    if (!_isMulti && hasOptions) {
      customValue = _options?.find((co) => co.value === _value);
    }

    if (_isMulti) {
      return (_value as string[])?.map((v) => {
        const _customValue = hasOptions
          ? _options?.find((co) => co.value === v)
          : undefined;

        return {
          value: hasOptions ? _customValue?.value : v,
          label: hasOptions ? _customValue?.label : v,
        };
      }) as DropdownOption[];
    }

    return {
      value: hasOptions ? customValue?.value : _value,
      label: hasOptions ? customValue?.label : _value,
    } as DropdownOption;
  };

  const sanitizeLabel = (label: string): string => {
    return label.replace(/\\"/g, '"');
  };

  const loadOptions = useCallback(
    // TODO: add debounce for this options loading
    async (inputValue: string): Promise<DropdownOption[]> => {
      const body = JSON.stringify({
        ...asyncBody,
        ...(asyncSearchField
          ? {
              [asyncSearchField]: asyncSearchSerializeFn
                ? asyncSearchSerializeFn(inputValue)
                : inputValue,
            }
          : undefined),
      });

      const response = await fetch(`${URL_PREFIX}/api/v1/${asyncEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      });

      const result: DataResponse<string[]> = await response.json();

      const _options = result.data
        .filter((r) => r !== '' && r !== null) // Hide empty values, #DSCCONTO-381
        .map((r) => ({ value: r, label: sanitizeLabel(r) }));

      if (initLoadRef.current) {
        onLoadOptions?.(_options as unknown as T[]);
        initLoadRef.current = false;
      }

      return _options;
    },
    [
      asyncBody,
      asyncEndpoint,
      asyncSearchField,
      asyncSearchSerializeFn,
      onLoadOptions,
    ],
  );

  const handleCreate = (inputValue: string): void => {
    if (
      creatableRegExpValidator &&
      !inputValue.match(creatableRegExpValidator)
    ) {
      setError(`Invalid email: ${inputValue}`);
      setTimeout(() => setError(''), 3000);

      return;
    }

    const newOption = createOption(inputValue);

    setCreatableOptions((prev) => [...prev, newOption]);
    handleChange([...(value as unknown as DropdownOption[]), newOption]);
  };

  const setClassnames = (
    readonly: boolean,
    creatable = false,
  ): ClassNamesConfig<DropdownOption, boolean, GroupBase<DropdownOption>> => {
    return {
      dropdownIndicator: () =>
        readonly || creatable ? 'invisible w-0 !p-0' : '',
      indicatorSeparator: () =>
        readonly || creatable ? 'invisible' : 'bg-level-04',
      clearIndicator: () =>
        readonly ? 'invisible' : '!text-white self-start hover:!text-subtle',
      multiValueRemove: () =>
        readonly
          ? 'invisible w-0 !p-0.5'
          : 'bg-level-02 hover:!bg-error-default hover:!text-white',
      multiValue: () => '!bg-level-02 !text-white',
      multiValueLabel: () => 'text-body-sm !text-white',
      control: () =>
        `text-body-sm !bg-level-04 !w-full !border !border-emphasis-default !text-white ${!isValid && '!bg-[#FF8C8C33] !border-white'}`,
      menuList: () => 'text-body-sm bg-level-03 w-full custom-scroll',
      singleValue: () => 'text-body-sm !text-white',
      placeholder: () => 'text-body-sm !text-white',
      input: () => '!text-white',
      option: ({ isSelected, isFocused }) => {
        if (isSelected) {
          return 'bg-primary-default !text-white';
        }

        if (isFocused) {
          return '!bg-primary-default !text-white';
        }

        return '!bg-level-03 !text-white';
      },
    };
  };

  if (isCreatable) {
    return (
      <>
        <CreatableSelect<DropdownOption, boolean, GroupBase<DropdownOption>>
          inputId="dropdown-input"
          className={className}
          classNames={setClassnames(isReadonly, true)}
          components={components}
          isClearable={true}
          isMulti={isMulti}
          noOptionsMessage={() => noOptionsMessage}
          onChange={(e) => handleChange(e as unknown as DropdownOption)}
          onCreateOption={handleCreate}
          options={creatableOptions as unknown as DropdownOption[]}
          placeholder={placeholder}
          ref={ref as DropdownSelectRef}
          // value={value as unknown as DropdownOption}
        />
        {error ? (
          <p className="mx-sm my-xs text-error-default">{error}</p>
        ) : null}
      </>
    );
  }

  if (isAsync) {
    return (
      <>
        <AsyncSelect<DropdownOption, boolean, GroupBase<DropdownOption>>
          inputId="dropdown-input"
          cacheOptions={true}
          className={className}
          classNames={setClassnames(isReadonly)}
          components={components}
          defaultOptions={true}
          defaultValue={getDefaultValue(
            isMulti,
            isMulti ? (value as string[]) : (value as string),
          )}
          isMulti={isMulti}
          loadOptions={loadOptions}
          menuPlacement="auto"
          noOptionsMessage={() => noOptionsMessage}
          onChange={(e) => handleChange(e as DropdownOption)}
          placeholder={placeholder}
          ref={ref as DropdownSelectRef}
          value={getDefaultValue(
            isMulti,
            isMulti ? (value as string[]) : (value as string),
          )}
        />
        {error ? (
          <p className="mx-sm my-xs text-error-default">{error}</p>
        ) : null}
      </>
    );
  }

  return (
    <>
      <Select<DropdownOption, boolean, GroupBase<DropdownOption>>
        inputId="dropdown-input"
        className={className}
        classNames={setClassnames(isReadonly)}
        components={components}
        defaultValue={
          value
            ? getDefaultValue(isMulti, value as string[], options)
            : undefined
        }
        isMulti={isMulti}
        isDisabled={isReadonly}
        isSearchable={isSearchable}
        menuPlacement="auto"
        noOptionsMessage={() => noOptionsMessage}
        onChange={(e) => handleChange(e as DropdownOption)}
        options={options}
        placeholder={placeholder}
        ref={ref as DropdownSelectRef}
      />
      {error ? <p className="mx-sm my-xs text-error-default">{error}</p> : null}
    </>
  );
}

DropdownInner.displayName = 'DropdownInner';

export const Dropdown = forwardRef(DropdownInner) as <
  T extends DropdownOption,
  Q = unknown,
>(
  props: DropdownProps<T, Q> & {
    ref?: React.ForwardedRef<SelectInstance<T, boolean, GroupBase<T>>>;
  },
) => ReturnType<typeof DropdownInner>;
